"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useBooking } from "../context";
import { type HeldCredit } from "../checkout";
import { spotsState, type ScheduleSession } from "../schedule";
import { AboutThisClass, useCancelNote, useClassWhen } from "./class-summary";
import { focusRing, primaryButton } from "./ui";

/**
 * One-tap booking for a student who already has credit for the class.
 * A native <dialog>: focus is trapped and Escape closes it. Bottom sheet on
 * phones, centred card on larger screens.
 */
export function QuickBookDialog({
  session,
  credit,
  creditsAfter,
  description,
  tags,
  otherWayHref,
  confirming,
  error,
  onConfirm,
  onClose,
}: {
  session: ScheduleSession | null;
  credit: HeldCredit | null;
  creditsAfter: number | null;
  description?: string | null;
  tags?: string[] | null;
  otherWayHref: string;
  confirming: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t, fmt, Link } = useBooking();
  const ref = useRef<HTMLDialogElement>(null);
  const open = !!session && !!credit;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const when = useClassWhen({
    startAt: session?.startTime ?? new Date().toISOString(),
    endAt: session?.endTime ?? new Date().toISOString(),
  });
  const cancelNote = useCancelNote({
    startAt: session?.startTime ?? new Date().toISOString(),
    type: session?.type,
    cancellation: session?.cancellation,
  });

  if (!session || !credit) return <dialog ref={ref} className="hidden" />;

  const spots = spotsState(session);
  const titleId = `quickbook-${session.id}`;

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className="m-0 mt-auto w-full max-w-none rounded-t-[24px] bg-surface p-0 text-ink shadow-overlay backdrop:bg-ink/40 md:m-auto md:max-w-md md:rounded-card"
    >
      <div className="flex flex-col gap-5 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="font-display text-2xl font-medium text-ink">
              {session.title}
            </h2>
            <p className="text-sm text-ink-soft">
              {when}
              {session.teacherName && ` · ${t("schedule.withTeacher", { name: session.teacherName })}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("checkout.close")}
            className={`-mr-2 -mt-1 flex size-11 shrink-0 items-center justify-center rounded-pill text-ashe hover:text-ink ${focusRing}`}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-0.5 rounded-[14px] border-2 border-studio-accent bg-studio-accent-soft px-4 py-3.5">
          <p className="text-[15px] font-semibold text-ink">{t("checkout.useCredit")}</p>
          <p className="text-[13px] text-ink-soft">
            {creditsAfter === null
              ? t("checkout.unlimitedCredit", { name: credit.title })
              : t("checkout.creditsAfter", { name: credit.title, count: creditsAfter })}
          </p>
        </div>

        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming}
            aria-busy={confirming || undefined}
            className={`${primaryButton} h-[52px] w-full text-base`}
          >
            {t("checkout.confirmSpot")}
          </button>
          <Link
            href={otherWayHref}
            className={`flex min-h-11 items-center justify-center rounded-pill text-sm font-semibold text-studio-ink hover:underline ${focusRing}`}
          >
            {t("checkout.payAnotherWay")}
          </Link>
        </div>

        <AboutThisClass session={{ description, tags }} />

        <p className="text-center text-xs text-ashe">
          {cancelNote}
          {spots.kind === "few" && ` · ${t("schedule.spotsLeft", { count: spots.count })}`}
        </p>
      </div>
    </dialog>
  );
}
